import React, {
  useState,
  useMemo,
  useEffect,
  useRef,
  useCallback,
} from "react";
import SEO from "../components/SEO";
import {
  AcademicCapIcon,
  TrophyIcon,
  MusicalNoteIcon,
  ClipboardDocumentListIcon,
  CalendarDaysIcon,
  HandRaisedIcon,
  SparklesIcon,
  BookOpenIcon,
  CpuChipIcon,
  CalculatorIcon,
  ComputerDesktopIcon,
} from "@heroicons/react/24/outline";
import { motion, AnimatePresence } from "framer-motion";

// Cloudinary configuration
const CLOUD_NAME = "ddvpczzng";
const CLOUDINARY_BASE = `https://res.cloudinary.com/${CLOUD_NAME}/image/upload`;

// Image transformation presets
const TRANSFORMATIONS = {
  banner: "f_auto,q_auto,w_800,c_fill,g_auto",
  card: "f_auto,q_auto,w_600,c_fill,g_auto",
  thumbnail: "f_auto,q_auto,w_400,c_fill,g_auto",
  background: "f_auto,q_auto,w_1800",
};

const ZIGZAG_ACHIEVEMENTS = [
  {
    title: "Higher Secondary Started (2017–2018)",
    description:
      "Since the launch of Higher Secondary in 2017–2018, the institution has achieved remarkable academic milestones within a short span. Our students have produced centum holders in public examinations, nurtured exceptional talents, and secured district-level rank positions in academics. Consistent mentoring and disciplined learning practices have played a vital role in these achievements. In addition to academics, students have excelled in sports, representing the school at district and higher levels. These accomplishments reflect the institution’s commitment to holistic education, leadership, and competitive excellence, laying strong foundations for future success.",
    image:
      "https://res.cloudinary.com/ddvpczzng/image/upload/v1769366290/1_kiagpq.png",
  },
  {
    title: "NEET Excellence",
    description:
      "Our focused mentoring, regular assessments, and expert guidance have helped students excel in the National Eligibility cum Entrance Test (NEET). Structured coaching, concept-based learning, and continuous performance analysis have strengthened students’ academic confidence. Dedicated faculty support and systematic revision strategies have played a crucial role in improving results year after year. Many students have secured qualifying scores and progressed towards admissions in reputed medical institutions. These achievements highlight the school’s commitment to nurturing medical aspirants with discipline, determination, and academic excellence.",
    image:
      "https://res.cloudinary.com/ddvpczzng/image/upload/v1769366290/2_n7gx7u.png",
  },
  {
    title: "JEE Achievements",
    description:
      "Students have consistently performed well in the Joint Entrance Examination (JEE), securing admissions in top engineering institutions. Strong conceptual training, regular problem-solving sessions, and exam-oriented practice have strengthened analytical and logical thinking skills. Continuous assessment and personalized mentoring have helped students improve accuracy and time management. The disciplined academic environment has enabled students to face competitive challenges with confidence. These accomplishments reflect the institution’s dedication to shaping future engineers through quality education and focused guidance.",
    image:
      "https://res.cloudinary.com/ddvpczzng/image/upload/v1769366290/3_wa49y2.png",
  },
  {
    title: "CA Foundation Success",
    description:
      "Commerce students have cleared the Chartered Accountancy (CA) Foundation examination with excellent results. Strong conceptual clarity in accounting, economics, and business laws has been built through systematic teaching and regular practice. Continuous assessments and focused revision sessions have helped students strengthen exam readiness. Faculty mentoring and disciplined study habits have played a key role in consistent performance. These achievements reflect the institution’s commitment to nurturing future finance and accounting professionals with confidence and competence.",
    image:
      "https://res.cloudinary.com/ddvpczzng/image/upload/v1769366290/4_whvf1h.png",
  },
  {
    title: "CMA Aspirants",
    description:
      "Cost and Management Accountant (CMA) aspirants received structured training and achieved notable success. Strong emphasis on cost analysis, financial management, and problem-solving techniques has enhanced conceptual understanding. Regular assessments and guided practice sessions have improved exam preparedness and confidence. Dedicated faculty support and systematic study planning have contributed to consistent performance. These outcomes reflect the institution’s commitment to developing skilled management accounting professionals with industry-relevant knowledge.",
    image:
      "https://res.cloudinary.com/ddvpczzng/image/upload/v1769366291/5_grlkv2.png",
  },
  {
    title: "CS Examination Results",
    description:
      "Company Secretary (CS) aspirants have cleared professional examinations with confidence and consistency. Focused training in corporate laws, governance, and compliance has strengthened conceptual clarity. Regular assessments, case-based learning, and structured revision have improved exam performance. Continuous mentoring and disciplined preparation have helped students handle professional-level challenges effectively. These results reflect the institution’s commitment to nurturing competent corporate professionals with strong ethical and academic foundations.",
    image:
      "https://res.cloudinary.com/ddvpczzng/image/upload/v1769366291/6_cyhgil.png",
  },
  {
    title: "Government Medical College Placements",
    description:
      "Students secured admissions in Government Medical Colleges, reflecting academic excellence and dedication. Consistent preparation, strong subject fundamentals, and exam-focused mentoring played a vital role in this achievement. Continuous assessment and personalized guidance helped students maintain focus and confidence throughout the competitive process. The disciplined academic environment supported students in achieving their medical career aspirations. This milestone highlights the institution’s commitment to guiding students towards prestigious professional pathways with determination and excellence.",
    image:
      "https://res.cloudinary.com/ddvpczzng/image/upload/v1769366291/7_iifffz.png",
  },
  {
    title: "Competitive Exam Achievers",
    description:
      "Students have successfully cleared competitive examinations conducted by SBI (State Bank of India), IBPS (Institute of Banking Personnel Selection), RRB (Regional Rural Bank), TNPSC (Tamil Nadu Public Service Commission), MHC (Madras High Court), and MRB (Medical Recruitment Board). Focused training in aptitude, reasoning, and general awareness has strengthened exam preparedness. Regular mock tests and performance analysis have helped students improve accuracy and time management. Continuous mentoring and disciplined study practices have enabled students to meet the demands of highly competitive selection processes. These achievements reflect the institution’s dedication to preparing students for responsible public service and professional careers.",
    image:
      "https://res.cloudinary.com/ddvpczzng/image/upload/v1769366292/8_wnqmwa.png",
  },
];

const Events = () => {
  const [activeTab, setActiveTab] = useState("events");
  const [modalImage, setModalImage] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Cloudinary image URLs with transformations
  const CLOUDINARY_IMAGES = useMemo(
    () => ({
      // Events
      sportsDay: `${CLOUDINARY_BASE}/${TRANSFORMATIONS.card}/v1757490860/Annual_Sports_Day_k1ncek.png`,
      Expo: `${CLOUDINARY_BASE}/${TRANSFORMATIONS.card}/v1757490860/Expo_httjtr.png`,
      annualDay: `${CLOUDINARY_BASE}/${TRANSFORMATIONS.card}/v1757490859/Annual_Day_Celebration_a5iorg.png`,

      kgDay: `${CLOUDINARY_BASE}/${TRANSFORMATIONS.card}/v1757490860/KG_Day_ynihap.png`,
      shareCareDay: `${CLOUDINARY_BASE}/${TRANSFORMATIONS.card}/v1757490861/Share_and_Care_Day_udvm0n.png`,
      GreenDay: `${CLOUDINARY_BASE}/${TRANSFORMATIONS.card}/v1757490860/Green_Day_byt80p.png`,

      // Extracurriculars
      bharathanatyam: `${CLOUDINARY_BASE}/${TRANSFORMATIONS.card}/v1757488366/Bharathanatyam_qgwlo7.png`,
      silambam: `${CLOUDINARY_BASE}/${TRANSFORMATIONS.card}/v1757488366/Silambam_ulhu9m.png`,
      karate: `${CLOUDINARY_BASE}/${TRANSFORMATIONS.card}/v1757488366/Karate_yszv5n.png`,
      yoga: `${CLOUDINARY_BASE}/${TRANSFORMATIONS.card}/v1757488366/Yoga_i86aqc.png`,

      // Co-curriculars
      Montessori: `${CLOUDINARY_BASE}/${TRANSFORMATIONS.card}/v1757493404/Montessori_wo5dr7.png`,
      Abacus: `${CLOUDINARY_BASE}/${TRANSFORMATIONS.card}/v1757493404/Abacus_mo9mll.png`,
      Robotics: `${CLOUDINARY_BASE}/${TRANSFORMATIONS.card}/v1757493404/Robotics_u9ro7k.png`,
      ArtCraft: `${CLOUDINARY_BASE}/${TRANSFORMATIONS.card}/v1757493404/Art_Craft_ugizmg.png`,

      blogBg: `${CLOUDINARY_BASE}/${TRANSFORMATIONS.background}/v1757446093/Blog_BG_ylfoqa.png`,

      // Achievement banners
      bannerLandscape1: `${CLOUDINARY_BASE}/${TRANSFORMATIONS.banner}/v1757446110/11th_fshyid.jpg`,
      bannerLandscape2: `${CLOUDINARY_BASE}/${TRANSFORMATIONS.banner}/v1757446104/whole_banner_wywxej.jpg`,
      bannerLandscape3: `${CLOUDINARY_BASE}/${TRANSFORMATIONS.banner}/v1757446092/10th_3_vttopz.jpg`,
      bannerLandscape4: `${CLOUDINARY_BASE}/${TRANSFORMATIONS.banner}/v1757446097/10th_ffft6f.jpg`,
      bannerLandscape5: `${CLOUDINARY_BASE}/${TRANSFORMATIONS.banner}/v1757446090/10th_2_mvc8nf.jpg`,
      bannerLandscape6: `${CLOUDINARY_BASE}/${TRANSFORMATIONS.banner}/v1757446096/10th_4_hlyw1r.jpg`,
      placeholder: `${CLOUDINARY_BASE}/${TRANSFORMATIONS.thumbnail}/v1754344428/website%20images/Lkg/20250709_133004_rdo6cv.jpg`,
    }),
    [],
  );

  const TAB_ORDER = ["events", "Curricular", "achievements", "groups"];

  const THEME_CONFIG = {
    events: {
      bgColor: "bg-pink-200",
      textColor: "text-gray-800",
      icon: <CalendarDaysIcon className="w-5 h-5" />,
    },
    Curricular: {
      bgColor: "bg-yellow-100",
      textColor: "text-gray-800",
      icon: <MusicalNoteIcon className="w-5 h-5" />,
    },
    achievements: {
      bgColor: "bg-green-100",
      textColor: "text-black",
      icon: <TrophyIcon className="w-5 h-5" />,
    },
    groups: {
      bgColor: "bg-orange-100",
      textColor: "text-black",
      icon: <ClipboardDocumentListIcon className="w-5 h-5" />,
    },
  };

  const upcomingEvents = useMemo(
    () => [
      {
        id: 1,
        title: "Annual Sports Day",
        icon: <TrophyIcon className="h-6 w-6 text-blue-600" />,
        image: CLOUDINARY_IMAGES.sportsDay,
      },
      {
        id: 2,
        title: "Expo",
        icon: <AcademicCapIcon className="h-6 w-6 text-green-600" />,
        image: CLOUDINARY_IMAGES.Expo,
      },
      {
        id: 3,
        title: "Annual Day Celebration",
        icon: <MusicalNoteIcon className="h-6 w-6 text-red-600" />,
        image: CLOUDINARY_IMAGES.annualDay,
      },
      {
        id: 4,
        title: "KG Day",
        icon: <AcademicCapIcon className="h-6 w-6 text-yellow-600" />,
        image: CLOUDINARY_IMAGES.kgDay,
      },
      {
        id: 5,
        title: "Share and Care Day",
        icon: <HandRaisedIcon className="h-6 w-6 text-purple-600" />,
        image: CLOUDINARY_IMAGES.shareCareDay,
      },
      {
        id: 6,
        title: "Green Day",
        icon: <SparklesIcon className="h-6 w-6 text-green-600" />,
        image: CLOUDINARY_IMAGES.GreenDay,
      },
    ],
    [CLOUDINARY_IMAGES],
  );

  const extracurricularActivities = useMemo(
    () => [
      {
        title: "Bharathanatyam",
        description:
          "Grace and tradition come alive through mesmerizing Bharathanatyam performances.",
        image: CLOUDINARY_IMAGES.bharathanatyam,
      },
      {
        title: "Silambam",
        description:
          "Ancient martial arts like Silambam instill discipline, agility, and cultural pride.",
        image: CLOUDINARY_IMAGES.silambam,
      },
      {
        title: "Karate",
        description:
          "Karate sessions that build confidence, strength, and self-defense techniques.",
        image: CLOUDINARY_IMAGES.karate,
      },
      {
        title: "Yoga",
        description:
          "Yoga promotes mindfulness, flexibility, and holistic student wellness.",
        image: CLOUDINARY_IMAGES.yoga,
      },
    ],
    [CLOUDINARY_IMAGES],
  );

  const cocurricularActivities = useMemo(
    () => [
      {
        title: "Montessori",
        description:
          "Activity-based labs that build practical, sensorial, cultural, creative, and social skills with academics.",
        image: CLOUDINARY_IMAGES.Montessori,
      },
      {
        title: "Abacus",
        description:
          "Fun, structured training to boost numerical skills and logical thinking.",
        image: CLOUDINARY_IMAGES.Abacus,
      },
      {
        title: "Robotics",
        description:
          "Hands-on platform to design, build, and program robots, enhancing creativity and problem-solving.",
        image: CLOUDINARY_IMAGES.Robotics,
      },
      {
        title: "Art & Craft",
        description:
          "Creative space for children to explore colors, imagination, and fine motor skills.",
        image: CLOUDINARY_IMAGES.ArtCraft,
      },
    ],
    [CLOUDINARY_IMAGES],
  );

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setTimeout(() => setModalImage(null), 300);
  }, []);

  const modalContainerRef = useRef(null);
  useEffect(() => {
    if (!isModalOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeModal();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow || "";
    };
  }, [isModalOpen, closeModal]);

  const EventsTab = () => (
    <section aria-labelledby="events-tab-heading">
      <h2 id="events-tab-heading" className="sr-only">
        Events
      </h2>
      <div className="text-center mb-8 sm:mb-12">
        <h3 className="text-2xl sm:text-3xl font-bold text-gray-900">
          Beyond the <span className="text-amber-600">Classrooms</span>
        </h3>
        <p className="text-gray-600 mt-2 max-w-2xl mx-auto text-sm sm:text-base">
          Our school empowers students through hands-on experiences and
          enriching activities.
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {upcomingEvents.map((event) => (
          <EventCard key={event.id} event={event} />
        ))}
      </div>
    </section>
  );

  const EventCard = ({ event }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-all duration-300 group"
    >
      <div className="h-48 sm:h-56 overflow-hidden relative">
        <img
          src={event.image}
          alt={event.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = CLOUDINARY_IMAGES.placeholder;
          }}
          loading="lazy"
          decoding="async"
          width={600}
          height={400}
        />
        <div
          className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300"
          aria-hidden="true"
        ></div>
      </div>
      <div className="p-4 sm:p-6 group-hover:bg-gray-50 transition-colors duration-300">
        <div className="flex items-center">
          {event.icon}
          <h4 className="text-lg sm:text-xl font-bold text-gray-900 group-hover:text-cyan-600 transition-colors duration-300 ml-2">
            {event.title}
          </h4>
        </div>
      </div>
    </motion.div>
  );

  const CurricularTab = () => (
    <section aria-labelledby="curricular-tab-heading">
      <h2 id="curricular-tab-heading" className="sr-only">
        Curricular
      </h2>

      <div className="text-center mb-8 sm:mb-12">
        <h3 className="text-2xl sm:text-3xl font-bold text-gray-900">
          Beyond the <span className="text-amber-600">Textbooks</span>
        </h3>
        <p className="text-gray-600 mt-2 max-w-2xl mx-auto text-sm sm:text-base">
          Our school fosters holistic development through a variety of
          extracurricular and co-curricular activities.
        </p>
      </div>

      <div className="flex items-center mb-6">
        <motion.div
          className="h-1 bg-amber-600 rounded-full mr-4"
          initial={{ width: 0 }}
          whileInView={{ width: 60 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          aria-hidden="true"
        />
        <motion.h3
          className="text-2xl sm:text-3xl font-bold text-gray-900"
          initial={{ x: -20, opacity: 0 }}
          whileInView={{ x: 0, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          Extracurriculars
        </motion.h3>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        {extracurricularActivities.map((activity, i) => (
          <ActivityCard key={`extra-${i}`} activity={activity} index={i} />
        ))}
      </div>

      <div className="flex items-center mb-6 mt-10">
        <motion.div
          className="h-1 bg-amber-600 rounded-full mr-4"
          initial={{ width: 0 }}
          whileInView={{ width: 60 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          aria-hidden="true"
        />
        <motion.h3
          className="text-2xl sm:text-3xl font-bold text-gray-900"
          initial={{ x: -20, opacity: 0 }}
          whileInView={{ x: 0, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          Co-curriculars
        </motion.h3>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        {cocurricularActivities.map((activity, i) => (
          <CoCurricularCard key={`co-${i}`} activity={activity} index={i} />
        ))}
      </div>
    </section>
  );

  const ActivityCard = ({ activity, index }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="max-w-sm bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden group hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
    >
      <div className="relative overflow-hidden">
        <img
          className="w-full h-40 sm:h-48 object-cover transition-transform duration-500 group-hover:scale-110"
          src={activity.image}
          alt={activity.title}
          onError={(e) => (e.target.src = CLOUDINARY_IMAGES.placeholder)}
          loading="lazy"
          decoding="async"
          width={600}
          height={400}
        />
        <div
          className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300"
          aria-hidden="true"
        ></div>
      </div>
      <div className="p-4 sm:p-5 group-hover:bg-gray-50 transition-colors duration-300">
        <h4 className="mb-2 text-lg sm:text-xl font-bold text-gray-900 group-hover:text-cyan-600 transition-colors duration-300">
          {activity.title}
        </h4>
        <p className="mb-3 font-normal text-gray-700 group-hover:text-gray-800 transition-colors duration-300 text-sm sm:text-base">
          {activity.description}
        </p>
      </div>
    </motion.div>
  );

  const CoCurricularCard = ({ activity, index }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="max-w-sm bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden group hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
    >
      <div className="relative overflow-hidden">
        <img
          className="w-full h-40 sm:h-48 object-cover transition-transform duration-500 group-hover:scale-110"
          src={activity.image}
          alt={activity.title}
          onError={(e) => (e.target.src = CLOUDINARY_IMAGES.placeholder)}
          loading="lazy"
          decoding="async"
          width={600}
          height={400}
        />
        <div
          className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300"
          aria-hidden="true"
        ></div>
      </div>
      <div className="p-4 sm:p-5 group-hover:bg-gray-50 transition-colors duration-300">
        <div className="flex items-center mb-2">
          {activity.icon}
          <h4 className="ml-2 text-lg sm:text-xl font-bold text-gray-900 group-hover:text-cyan-600 transition-colors duration-300">
            {activity.title}
          </h4>
        </div>
        <p className="mb-3 font-normal text-gray-700 group-hover:text-gray-800 transition-colors duration-300 text-sm sm:text-base">
          {activity.description}
        </p>
      </div>
    </motion.div>
  );
  const ZigZagAchievements = () => (
    <section className="space-y-20">
      {ZIGZAG_ACHIEVEMENTS.map((item, index) => {
        const isReverse = index % 2 !== 0;

        return (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex flex-col lg:flex-row items-center gap-8"
          >
            {/* Image */}
            <div
              className={`w-full lg:w-1/2 ${
                isReverse ? "order-1 lg:order-2" : "order-1"
              }`}
            >
              <div className="rounded-2xl overflow-hidden transition-all duration-300 hover:shadow-2xl">
                <img
                  src={item.image}
                  alt={item.title}
                  className="w-full h-72 sm:h-80 object-cover transition-transform duration-500 hover:scale-105"
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </div>

            {/* Content */}
            <div
              className={`w-full lg:w-1/2 text-center lg:text-left ${
                isReverse ? "order-2 lg:order-1" : "order-2"
              }`}
            >
              <h4 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3">
                {item.title}
              </h4>
              <p className="text-gray-700 text-sm sm:text-base leading-relaxed text-justify">
                {item.description}
              </p>
            </div>
          </motion.div>
        );
      })}
    </section>
  );

  const AchievementsTab = () => (
    <section
      className={THEME_CONFIG.achievements.textColor}
      aria-labelledby="achievements-tab-heading"
    >
      <h2 id="achievements-tab-heading" className="sr-only">
        Achievements
      </h2>

      <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center">
        Pride <span className="text-amber-600">then</span>, Power{" "}
        <span className="text-amber-600">now!</span>
      </h3>

      <p className="text-gray-600 mt-1 max-w-2xl mx-auto text-center mb-12 sm:mb-20 text-sm sm:text-base">
        Our school celebrates excellence through a journey of remarkable
        achievements.
      </p>

      {/* ✅ Zig-Zag Blog Section */}
      <ZigZagAchievements />
    </section>
  );

  const GROUPS = [
    {
      id: 1,
      title: "Group 1",
      subtitle: "Science (Biology)",
      icon: <BookOpenIcon className="w-12 h-12 text-green-500" />,
      color: "bg-gradient-to-r from-green-400 to-teal-400",
      buttonColor: "bg-gradient-to-r from-green-400 to-teal-400",
      subjects: ["Maths", "Physics", "Chemistry", "Biology"],
      formLink:
        "https://docs.google.com/forms/d/e/1FAIpQLSdW5AwmV3DSirB38HJofn3US9UFnEAHIaUji_2Ayd4a8AZ92Q/viewform",
    },
    {
      id: 2,
      title: "Group 2",
      subtitle: "Science (Computer Science)",
      icon: <ComputerDesktopIcon className="w-12 h-12 text-blue-500" />,
      color: "bg-gradient-to-r from-blue-400 to-cyan-400",
      buttonColor: "bg-gradient-to-r from-blue-400 to-cyan-400",
      subjects: ["Maths", "Physics", "Chemistry", "Computer Science"],
      formLink:
        "https://docs.google.com/forms/d/e/1FAIpQLSdW5AwmV3DSirB38HJofn3US9UFnEAHIaUji_2Ayd4a8AZ92Q/viewform",
    },
    {
      id: 3,
      title: "Group 3",
      subtitle: "Commerce (Computer Application)",
      icon: <CpuChipIcon className="w-12 h-12 text-purple-500" />,
      color: "bg-gradient-to-r from-purple-500 to-pink-500",
      buttonColor: "bg-gradient-to-r from-purple-500 to-pink-500",
      subjects: [
        "Commerce",
        "Economics",
        "Accountancy",
        "Computer Application",
      ],
      formLink:
        "https://docs.google.com/forms/d/e/1FAIpQLSdW5AwmV3DSirB38HJofn3US9UFnEAHIaUji_2Ayd4a8AZ92Q/viewform",
    },
    {
      id: 4,
      title: "Group 4",
      subtitle: "Commerce (Business Maths)",
      icon: <CalculatorIcon className="w-12 h-12 text-orange-500" />,
      color: "bg-gradient-to-r from-orange-400 to-red-400",
      buttonColor: "bg-gradient-to-r from-orange-400 to-red-400",
      subjects: ["Commerce", "Economics", "Accountancy", "Business Maths"],
      formLink:
        "https://docs.google.com/forms/d/e/1FAIpQLSdW5AwmV3DSirB38HJofn3US9UFnEAHIaUji_2Ayd4a8AZ92Q/viewform",
    },
  ];

  const GroupsTab = () => (
    <section
      className={`text-center ${THEME_CONFIG.groups.textColor}`}
      aria-labelledby="groups-tab-heading"
    >
      <h2 id="groups-tab-heading" className="sr-only">
        Academic groups
      </h2>

      <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center">
        Academic <span className="text-amber-600">groups</span>
      </h3>
      <p className="text-gray-600 mt-1 max-w-2xl mx-auto mb-14 sm:mb-20 text-sm sm:text-base text-center">
        Our school offers diverse academic groups to cater to different
        interests and career paths.
      </p>

      {/* Updated grid for 2x2 on mobile */}
      <div
        className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 
                gap-x-4 sm:gap-x-6 lg:gap-x-8 
                gap-y-10 sm:gap-y-14 
                max-w-6xl mx-auto px-4"
      >
        {GROUPS.map((group) => (
          <motion.div
            key={group.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: group.id * 0.1 }}
            className="group"
          >
            <GroupCard {...group} />
          </motion.div>
        ))}
      </div>
    </section>
  );

  const GroupCard = ({
    id,
    title,
    subtitle,
    icon,
    color,
    buttonColor,
    subjects,
    formLink,
  }) => (
    <div className="relative bg-white rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 w-full mx-auto transform hover:-translate-y-2">
      <div className="absolute -top-8 sm:-top-12 left-1/2 transform -translate-x-1/2 w-20 h-20 sm:w-28 sm:h-28 bg-white rounded-full shadow-xl flex items-center justify-center transition-transform duration-300 group-hover:scale-110 z-10">
        <div className="w-16 h-16 sm:w-24 sm:h-24 rounded-full flex items-center justify-center">
          {React.cloneElement(icon, {
            className: "w-8 h-8 sm:w-12 sm:h-12",
          })}
        </div>
      </div>

      {/* Card content with responsive height */}
      <div className="pt-16 sm:pt-20 pb-4 sm:pb-6 px-3 sm:px-6 flex flex-col items-center min-h-[340px] sm:min-h-[400px]">
        {/* Title section with fixed height */}
        <div className="text-center mb-3 sm:mb-4 min-h-[50px] sm:min-h-[60px] flex flex-col justify-center w-full">
          <h4 className="text-base sm:text-xl font-bold text-gray-900 mb-1 line-clamp-1">
            {title}
          </h4>
          <p className="text-xs sm:text-sm text-gray-600 line-clamp-2 px-1">
            {subtitle}
          </p>
        </div>

        {/* Color band */}
        <div className={`w-full h-1 rounded-full ${color} mb-4 sm:mb-6`}></div>

        {/* Subjects list with fixed height */}
        <div className="mb-4 sm:mb-6 w-full flex-grow min-h-[100px] sm:min-h-[120px]">
          <ul className="space-y-1 sm:space-y-2 h-full flex flex-col justify-center">
            {subjects.map((subject, index) => (
              <li
                key={index}
                className="text-center text-gray-700 text-xs sm:text-sm md:text-base line-clamp-1"
              >
                {subject}
              </li>
            ))}
          </ul>
        </div>

        {/* Register button - always at bottom */}
        <div className="w-full mt-auto">
          <button
            onClick={() => window.open(formLink, "_blank")}
            className={`w-full py-2 sm:py-3 px-3 sm:px-4 ${buttonColor} text-white font-medium rounded-xl shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105 active:scale-95 flex items-center justify-center gap-1 sm:gap-2 text-sm sm:text-base`}
          >
            <svg
              className="w-4 h-4 sm:w-5 sm:h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <span>Register</span>
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div
      className={`min-h-screen transition-colors duration-500 ${THEME_CONFIG[activeTab].bgColor}`}
    >
      <SEO
        title="Events, Activities & Academic Groups"
        description="Explore the vibrant events, extracurricular activities, co-curriculars, and academic groups at St. Thomas Vidyalayam Matric Higher Secondary School."
        keywords="School Events, Annual Day, Sports Day, Bharathanatyam, Silambam, Academic Groups"
        canonicalPath="/blog"
      />

      <h1 className="sr-only">Events — STVMHS</h1>

      <div
        className="relative text-white py-20 sm:py-28 bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url(${CLOUDINARY_IMAGES.blogBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          width: "100%",
        }}
        aria-label="Events hero"
      >
        <div className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 text-center">
          <motion.h1>
            Our <span className="text-amber-400">School</span>
          </motion.h1>

          <div
            className="flex flex-wrap justify-center gap-3 sm:gap-4 mt-8"
            role="tablist"
            aria-label="Events tabs"
          >
            {TAB_ORDER.map((tab, i) => (
              <TabButton
                key={tab}
                tab={tab}
                activeTab={activeTab}
                onClick={() => setActiveTab(tab)}
                config={THEME_CONFIG[tab]}
                index={i}
              />
            ))}
          </div>
        </div>
      </div>

      <main
        className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12 transition-opacity duration-500 ease-in-out"
        aria-live="polite"
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            role="tabpanel"
            aria-labelledby={`${activeTab}-tab`}
          >
            {activeTab === "events" && <EventsTab />}
            {activeTab === "Curricular" && <CurricularTab />}
            {activeTab === "achievements" && <AchievementsTab />}
            {activeTab === "groups" && <GroupsTab />}
          </motion.div>
        </AnimatePresence>
      </main>

      <ImageModal
        isOpen={isModalOpen}
        image={modalImage}
        onClose={closeModal}
        containerRef={modalContainerRef}
      />
    </div>
  );
};

const TabButton = ({ tab, index, activeTab, onClick, config }) => {
  const isActive = activeTab === tab;
  return (
    <motion.button
      id={`${tab}-tab`}
      type="button"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      onClick={onClick}
      className={`w-36 sm:w-44 h-12 sm:h-16 flex items-center justify-center cursor-pointer transition-all duration-300 font-medium relative rounded-lg sm:rounded-xl ${
        isActive
          ? `${config.bgColor} text-black scale-105 shadow-lg`
          : "bg-white/90 text-black hover:scale-105"
      }`}
      whileHover={{ scale: 1.05 }}
      role="tab"
      aria-selected={isActive ? "true" : "false"}
      aria-controls={`${tab}-panel`}
    >
      <span className="relative z-10 flex items-center gap-2">
        {config.icon}
        <span className="text-sm sm:text-base">
          {tab === "Curricular"
            ? "Curricular"
            : tab.charAt(0).toUpperCase() + tab.slice(1)}
        </span>
      </span>
    </motion.button>
  );
};

const ImageModal = ({ isOpen, image, onClose, containerRef }) => (
  <AnimatePresence>
    {isOpen && (
      <motion.div
        ref={containerRef}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 cursor-zoom-out p-4"
        role="dialog"
        aria-modal="true"
        aria-label="Image viewer"
      >
        <motion.img
          initial={{ scale: 0.9 }}
          animate={{ scale: 1 }}
          exit={{ scale: 0.9 }}
          src={image || ""}
          alt="Full view"
          className="max-w-full max-h-full rounded-lg shadow-2xl"
          decoding="async"
          width={1200}
          height={900}
          onClick={(e) => e.stopPropagation()}
        />
      </motion.div>
    )}
  </AnimatePresence>
);

export default React.memo(Events);
